import { createClient } from '@supabase/supabase-js';

// Get environment variables with fallback to mock mode
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if we have real Supabase credentials
const isRealSupabase = supabaseUrl && supabaseAnonKey && 
  supabaseUrl !== 'your_supabase_url' && 
  supabaseAnonKey !== 'your-anon-key';

let supabaseInstance = null;

// Initialize Supabase client
const getSupabase = () => {
  if (isRealSupabase) {
    if (!supabaseInstance) {
      supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
        global: {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
          },
        },
        db: {
          schema: 'public',
        },
      });
    }
    return supabaseInstance;
  }

  // Mock client for development when env vars not set
  console.warn('⚠️ Using mock Supabase client. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY for real integration.');
  return {
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      signInWithPassword: async () => ({ data: { user: null }, error: null }),
      signOut: async () => ({ error: null }),
      onAuthStateChange: (callback) => {
        callback('SIGNED_OUT', null);
        return { data: { subscription: { unsubscribe: () => {} } } };
      },
      signInWithOAuth: async () => ({ data: { url: null }, error: null }),
    },
    from: (table) => ({
      select: (columns = '*') => ({
        eq: (column, value) => ({
          single: () => ({ data: null, error: { code: 'PGRST116', message: 'Not found' } }),
          order: (column, options = { ascending: true }) => ({ data: [], error: null }),
          data: [],
          error: null,
        }),
        order: (column, options = { ascending: true }) => ({ data: [], error: null }),
        data: [],
        error: null,
      }),
      insert: (values) => ({
        select: (columns = '*') => ({
          single: () => ({ data: null, error: null }),
        }),
        data: null,
        error: null,
      }),
      update: (values) => ({
        eq: (column, value) => ({
          select: (columns = '*') => ({
            single: () => ({ data: null, error: null }),
          }),
          data: null,
          error: null,
        }),
      }),
      delete: () => ({
        eq: (column, value) => ({ data: null, error: null }),
      }),
    }),
    storage: {
      from: () => ({
        upload: async () => ({ data: null, error: null }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
      }),
    },
  };
};

export { getSupabase };

// ============================================
// SIWE Authentication Functions
// ============================================

/**
 * Generate a random alphanumeric string for SIWE nonce
 * SIWE requires nonce to be alphanumeric (no hyphens or special characters)
 * @param {number} length - Length of the nonce (default: 32)
 * @returns {string} Alphanumeric nonce
 */
const generateAlphanumericNonce = (length = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let nonce = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  
  for (let i = 0; i < length; i++) {
    nonce += chars[randomValues[i] % chars.length];
  }
  
  return nonce;
};

/**
 * Generate a nonce for SIWE authentication
 * Stores nonce in Supabase with 5-minute expiration
 */
export const generateNonce = async () => {
  const supabase = getSupabase();
  
  if (!isRealSupabase) {
    // Mock nonce for development - alphanumeric only
    const mockNonce = generateAlphanumericNonce(16);
    return { data: { nonce: mockNonce }, error: null };
  }

  try {
    // Generate alphanumeric nonce (SIWE requires alphanumeric, no hyphens)
    // Minimum 8 characters, using 32 for better security
    const nonce = generateAlphanumericNonce(32);
    
    // Store nonce temporarily (you can create a nonces table if needed)
    // For MVP, we'll use Supabase's built-in SIWE support
    return { data: { nonce }, error: null };
  } catch (error) {
    console.error('Error generating nonce:', error);
    return { data: null, error: { message: 'Failed to generate nonce' } };
  }
};

/**
 * Sign in with Ethereum using SIWE
 * @param {string} address - Wallet address
 * @param {string} signature - Message signature
 * @param {string} message - SIWE message
 */
export const signInWithWallet = async (address, signature, message) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Signing in with wallet', address);
    return { 
      data: { 
        user: { id: 'demo-user', address },
        session: { access_token: 'mock-token' }
      }, 
      error: null 
    };
  }

  try {
    // For MVP: Use a simplified wallet-based auth approach
    // Since Supabase Auth doesn't natively support wallet addresses,
    // we'll check for existing wallet and create a minimal user record
    
    // Normalize address
    const normalizedAddress = address.toLowerCase();
    
    // For MVP: Use wallet address as user ID
    // Note: Database operations will fail due to RLS policies requiring auth.users,
    // but we'll handle this gracefully and allow wallet-based auth to work
    
    const userId = normalizedAddress; // Use wallet address as user ID for MVP
    
    // Try to check if wallet exists (will likely fail due to RLS, but that's OK)
    // Suppress errors since they're expected without proper auth session
    try {
      const { data: existingWallet, error: walletCheckError } = await supabase
        .from('wallets')
        .select('user_id, address')
        .eq('address', normalizedAddress)
        .maybeSingle();

      // If wallet exists and we got a valid user_id, use it
      if (existingWallet && existingWallet.user_id && !walletCheckError) {
        // Only use it if it's a valid UUID format (not a wallet address)
        if (existingWallet.user_id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          return {
            data: {
              user: { id: existingWallet.user_id, address: normalizedAddress },
              session: null,
            },
            error: null,
          };
        }
      }
    } catch (error) {
      // RLS or other errors - continue with wallet address as user ID
      // Silently handle - these errors are expected without auth session
    }

    // Return user data with wallet address as ID
    // Database operations will fail, but wallet-based auth will work
    return {
      data: {
        user: { id: userId, address: normalizedAddress },
        session: null, // No Supabase Auth session for wallet-based auth
      },
      error: null,
    };
  } catch (error) {
    console.error('Error signing in with wallet:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Sign out current user
 */
export const signOut = async () => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Signing out');
    return { error: null };
  }

  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    console.error('Error signing out:', error);
    return { error: { message: error.message } };
  }
};

// ============================================
// Profile Functions
// ============================================

/**
 * Get profile by user ID
 */
export const getProfile = async (userId) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Getting profile for', userId);
    if (userId === 'demo' || userId === 'demo-user') {
      return {
        data: {
          id: 'demo-id',
          username: 'demo',
          display_name: 'Demo User',
          bio: 'This is a demo profile to showcase HubID capabilities',
          avatar: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3e%3crect width='100%25' height='100%25' fill='%237E3AF2'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='40'%3eDemo%3c/text%3e%3c/svg%3e",
          wallet_address: '0x0000000000000000000000000000000000000000',
          theme: 'dark-pro',
          is_public: true,
        },
        error: null
      };
    }
    return { data: null, error: { code: 'PGRST116' } };
  }

  try {
    // Check if userId is a wallet address (starts with 0x) - if so, query by wallet_address instead
    const isWalletAddress = userId && userId.startsWith('0x');
    
    let data, error;
    if (isWalletAddress) {
      // Query by wallet_address instead of user_id for wallet-based auth
      const result = await supabase
        .from('profiles')
        .select('*')
        .eq('wallet_address', userId.toLowerCase())
        .maybeSingle(); // Use maybeSingle() to avoid errors when not found
      data = result.data;
      error = result.error;
    } else {
      // Query by user_id (UUID format)
      const result = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(); // Use maybeSingle() to avoid errors when not found
      data = result.data;
      error = result.error;
    }

    // Handle 406 (Not Acceptable) and 400 (Bad Request) errors
    if (error) {
      if (error.code === 'PGRST116' || 
          error.message?.includes('406') || 
          error.message?.includes('400') ||
          error.message?.includes('Not Acceptable')) {
        return { data: null, error: { code: 'PGRST116' } };
      }
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error getting profile:', error);
    // Return as "not found" for any errors
    return { data: null, error: { code: 'PGRST116' } };
  }
};

/**
 * Get profile by username
 */
export const getProfileByUsername = async (username) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Getting profile by username', username);
    return {
      data: {
        id: 'demo-id',
        username: username || 'demo',
        display_name: 'Demo User',
        bio: 'This is a demo profile to showcase HubID capabilities',
        avatar: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3e%3crect width='100%25' height='100%25' fill='%237E3AF2'/%3e%3ctext x='50%25' y='50%25' fill='white' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='40'%3eDemo%3c/text%3e%3c/svg%3e",
        wallet_address: '0x0000000000000000000000000000000000000000',
        theme: 'dark-pro',
        is_public: true,
      },
      error: null
    };
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username)
      .eq('is_public', true)
      .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors when not found

    if (error) {
      // Handle 406 errors (Not Acceptable) - might be RLS or header issue
      if (error.code === 'PGRST116' || error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
        return { data: null, error: { code: 'PGRST116', message: 'Profile not found' } };
      }
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error getting profile by username:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Create a new profile
 */
export const createProfile = async (profileData) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Creating profile');
    return { data: { ...profileData, id: 'new-profile-id' }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .insert(profileData)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error creating profile:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Update profile
 */
export const updateProfile = async (userId, profileData) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Updating profile for', userId);
    return { data: { ...profileData, id: userId }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...profileData, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Check if username is available
 */
export const isUsernameAvailable = async (username) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: { available: true }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', username)
      .maybeSingle();

    if (error && error.code === 'PGRST116') {
      return { data: { available: true }, error: null };
    }

    return { data: { available: !data }, error: null };
  } catch (error) {
    console.error('Error checking username:', error);
    return { data: { available: false }, error: { message: error.message } };
  }
};

// ============================================
// Blocks Functions
// ============================================

/**
 * Get blocks for a profile
 */
export const getBlocks = async (profileId) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Getting blocks for', profileId);
    if (profileId === 'demo-id' || profileId === 'new-profile-id') {
      return {
        data: [
          {
            id: '1',
            type: 'link',
            data: { title: 'Follow me on Twitter', url: '#', icon: '🐦' }
          },
          {
            id: '2',
            type: 'link',
            data: { title: 'Check out my GitHub', url: '#', icon: '💻' }
          },
          {
            id: '3',
            type: 'image',
            data: {
              url: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3e%3crect width='100%25' height='100%25' fill='%23E0E0E0'/%3e%3ctext x='50%25' y='50%25' fill='black' text-anchor='middle' dy='.3em' font-family='sans-serif' font-size='30'%3eDemo Image%3c/text%3e%3c/svg%3e",
              title: 'Demo Image'
            }
          },
        ],
        error: null
      };
    }
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from('blocks')
      .select('*')
      .eq('profile_id', profileId)
      .order('order_index', { ascending: true });

    return { data: data || [], error };
  } catch (error) {
    console.error('Error getting blocks:', error);
    return { data: [], error: { message: error.message } };
  }
};

/**
 * Update blocks for a profile (replace all)
 */
export const updateBlocks = async (profileId, blocks) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Updating blocks for', profileId);
    return { data: blocks, error: null };
  }

  try {
    // Delete existing blocks
    await supabase
      .from('blocks')
      .delete()
      .eq('profile_id', profileId);

    // Insert new blocks
    if (blocks && blocks.length > 0) {
      const blocksToInsert = blocks.map((block, index) => ({
        profile_id: profileId,
        type: block.type,
        data: block.data,
        order_index: index,
      }));

      const { data, error } = await supabase
        .from('blocks')
        .insert(blocksToInsert)
        .select();

      return { data, error };
    }

    return { data: [], error: null };
  } catch (error) {
    console.error('Error updating blocks:', error);
    return { data: null, error: { message: error.message } };
  }
};

// ============================================
// Analytics Functions
// ============================================

/**
 * Record a profile view
 */
export const recordView = async (profileId, viewerWallet = null) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Recording view for', profileId);
    return { data: null, error: null };
  }

  try {
    // Hash IP address for privacy (simplified - in production, do this server-side)
    const viewerIp = 'hashed-ip'; // Would be hashed server-side

    const { data, error } = await supabase
      .from('views')
      .insert({
        profile_id: profileId,
        viewer_wallet: viewerWallet,
        viewer_ip: viewerIp,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });

    return { data, error };
  } catch (error) {
    console.error('Error recording view:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Get view statistics for a profile
 */
export const getViewStats = async (profileId) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Getting view stats for', profileId);
    return { data: [], error: null };
  }

  try {
    // Get total views
    const { count: totalViews } = await supabase
      .from('views')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', profileId);

    // Get 7-day views
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const { count: views7d } = await supabase
      .from('views')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', profileId)
      .gte('created_at', sevenDaysAgo.toISOString());

    return {
      data: {
        total_views: totalViews || 0,
        views_7d: views7d || 0,
      },
      error: null
    };
  } catch (error) {
    console.error('Error getting view stats:', error);
    return { data: null, error: { message: error.message } };
  }
};

// ============================================
// Leads Functions
// ============================================

/**
 * Submit a lead (contact form)
 */
export const submitLead = async (profileId, leadData) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Submitting lead', leadData);
    return { data: { id: 'lead-id' }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('leads')
      .insert({
        profile_id: profileId,
        ...leadData,
      })
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error submitting lead:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Get leads for a profile
 */
export const getLeads = async (profileId, limit = 50) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false })
      .limit(limit);

    return { data, error };
  } catch (error) {
    console.error('Error getting leads:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Get lead count for a profile
 */
export const getLeadCount = async (profileId) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: { count: 0 }, error: null };
  }

  try {
    const { count, error } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', profileId);

    return { data: { count: count || 0 }, error };
  } catch (error) {
    console.error('Error getting lead count:', error);
    return { data: { count: 0 }, error: { message: error.message } };
  }
};

// ============================================
// Connections Functions
// ============================================

/**
 * Create a connection between two profiles
 * 
 * @param {string} profileId1 - First profile ID
 * @param {string} profileId2 - Second profile ID
 * @param {string} wallet1 - First wallet address (optional)
 * @param {string} wallet2 - Second wallet address (optional)
 * @param {string} notes - Notes about the connection (optional)
 * @param {Array<string>} tags - Tags for the connection (optional)
 * @param {Object} meetingContext - Meeting context {event, location, date} (optional)
 */
export const createConnection = async (profileId1, profileId2, wallet1 = null, wallet2 = null, notes = null, tags = null, meetingContext = null) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Creating connection', profileId1, profileId2);
    return { data: { id: 'connection-id' }, error: null };
  }

  try {
    // Check if connection already exists
    const { data: existing } = await supabase
      .from('connections')
      .select('id')
      .or(`and(profile_id_1.eq.${profileId1},profile_id_2.eq.${profileId2}),and(profile_id_1.eq.${profileId2},profile_id_2.eq.${profileId1})`)
      .single();

    if (existing) {
      return { data: existing, error: null };
    }

    const { data, error } = await supabase
      .from('connections')
      .insert({
        profile_id_1: profileId1,
        profile_id_2: profileId2,
        wallet_1: wallet1,
        wallet_2: wallet2,
        notes: notes || null,
        tags: tags || null,
        meeting_context: meetingContext || null,
      })
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error creating connection:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Update a connection (notes, tags, meeting context)
 */
export const updateConnection = async (connectionId, updates) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Updating connection', connectionId);
    return { data: { id: connectionId, ...updates }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('connections')
      .update(updates)
      .eq('id', connectionId)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error updating connection:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Search connections by tags
 */
export const searchConnectionsByTags = async (profileId, tags) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from('connections')
      .select(`
        *,
        profile_1:profiles!connections_profile_id_1_fkey(id, username, display_name, avatar),
        profile_2:profiles!connections_profile_id_2_fkey(id, username, display_name, avatar)
      `)
      .or(`profile_id_1.eq.${profileId},profile_id_2.eq.${profileId}`)
      .contains('tags', tags)
      .order('connected_at', { ascending: false });

    return { data, error };
  } catch (error) {
    console.error('Error searching connections by tags:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Get connection statistics by tags
 */
export const getConnectionStatsByTags = async (profileId) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: [], error: null };
  }

  try {
    // This would ideally use the database function, but we can do it client-side
    const { data: connections, error } = await supabase
      .from('connections')
      .select('tags')
      .or(`profile_id_1.eq.${profileId},profile_id_2.eq.${profileId}`)
      .not('tags', 'is', null);

    if (error) {
      throw error;
    }

    // Aggregate tags
    const tagCounts = {};
    connections.forEach(conn => {
      if (conn.tags && Array.isArray(conn.tags)) {
        conn.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });

    const stats = Object.entries(tagCounts).map(([tag, count]) => ({ tag, count }));
    stats.sort((a, b) => b.count - a.count);

    return { data: stats, error: null };
  } catch (error) {
    console.error('Error getting connection stats by tags:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Get connections for a profile
 */
export const getConnections = async (profileId) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from('connections')
      .select(`
        *,
        profile_1:profiles!connections_profile_id_1_fkey(id, username, display_name, avatar),
        profile_2:profiles!connections_profile_id_2_fkey(id, username, display_name, avatar)
      `)
      .or(`profile_id_1.eq.${profileId},profile_id_2.eq.${profileId}`)
      .order('connected_at', { ascending: false });

    return { data, error };
  } catch (error) {
    console.error('Error getting connections:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Check if two profiles are connected
 */
export const areConnected = async (profileId1, profileId2) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: { connected: false }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('connections')
      .select('id')
      .or(`and(profile_id_1.eq.${profileId1},profile_id_2.eq.${profileId2}),and(profile_id_1.eq.${profileId2},profile_id_2.eq.${profileId1})`)
      .single();

    return { data: { connected: !!data }, error };
  } catch (error) {
    if (error.code === 'PGRST116') {
      return { data: { connected: false }, error: null };
    }
    return { data: { connected: false }, error: { message: error.message } };
  }
};

// ============================================
// Link Clicks Functions
// ============================================

/**
 * Record a link click
 */
export const recordLinkClick = async (profileId, blockId, blockType, clickUrl, visitorWallet = null) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Recording link click', blockId);
    return { data: { id: 'click-id' }, error: null };
  }

  try {
    const referrer = document.referrer || null;
    const userAgent = navigator.userAgent;

    const { data, error } = await supabase
      .from('link_clicks')
      .insert({
        profile_id: profileId,
        block_id: blockId,
        block_type: blockType,
        click_url: clickUrl,
        visitor_wallet: visitorWallet,
        referrer: referrer,
        user_agent: userAgent,
      })
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error recording link click:', error);
    return { data: null, error: { message: error.message } };
  }
};

/**
 * Get click statistics for a profile
 */
export const getClickStats = async (profileId, days = 30) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    return { data: { total: 0, by_type: {}, by_day: [] }, error: null };
  }

  try {
    const since = new Date();
    since.setDate(since.getDate() - days);

    // Total clicks
    const { count: total } = await supabase
      .from('link_clicks')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', profileId)
      .gte('created_at', since.toISOString());

    // Clicks by type
    const { data: byTypeData } = await supabase
      .from('link_clicks')
      .select('block_type')
      .eq('profile_id', profileId)
      .gte('created_at', since.toISOString());

    const byType = (byTypeData || []).reduce((acc, click) => {
      acc[click.block_type] = (acc[click.block_type] || 0) + 1;
      return acc;
    }, {});

    // Clicks by day
    const { data: byDayData } = await supabase
      .from('link_clicks')
      .select('created_at')
      .eq('profile_id', profileId)
      .gte('created_at', since.toISOString())
      .order('created_at', { ascending: true });

    const byDay = (byDayData || []).reduce((acc, click) => {
      const date = new Date(click.created_at).toISOString().split('T')[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    return {
      data: {
        total: total || 0,
        by_type: byType,
        by_day: Object.entries(byDay).map(([date, count]) => ({ date, count })),
      },
      error: null,
    };
  } catch (error) {
    console.error('Error getting click stats:', error);
    return { data: null, error: { message: error.message } };
  }
};

// ============================================
// Wallet Functions
// ============================================

/**
 * Create or update wallet record
 */
export const upsertWallet = async (userId, address, ensName = null) => {
  const supabase = getSupabase();

  if (!isRealSupabase) {
    console.log('Mock: Upserting wallet', address);
    return { data: { id: 'wallet-id', user_id: userId, address }, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('wallets')
      .upsert({
        user_id: userId,
        address: address.toLowerCase(),
        ens_name: ensName,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'address',
      })
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error upserting wallet:', error);
    return { data: null, error: { message: error.message } };
  }
};
